<template>
  <!-- Contador a la izquierda y botones centrados; en móvil se apilan -->
  <div class="flex flex-col items-center gap-3 pt-10 md:grid md:grid-cols-[1fr_auto_1fr] md:items-center">
    <!-- Contador de registros (total del conjunto filtrado, no solo de la página) -->
    <p v-if="hasPaginationInfo" class="text-sm text-gray-600 md:justify-self-start" aria-live="polite">
      <template v-if="paginationInfo.total === 0">{{ $t('common.pagination.empty') }}</template>
      <template v-else>
        {{ $t('common.pagination.showing', {
          from: rangeFrom,
          to: rangeTo,
          total: paginationInfo.total,
          records: $t('common.pagination.records', paginationInfo.total)
        }) }}
      </template>
    </p>
    <span v-else class="hidden md:block" aria-hidden="true"></span>

  <nav class="flex flex-wrap items-center justify-center gap-2" aria-label="Pagination">
    <!-- Botón de anterior -->
    <button
      @click="goToPreviousPage"
      :disabled="modelValue === 1"
      class="px-4 h-8 flex items-center justify-center rounded-full border border-[#2AB4D9] bg-[#2AB4D9] text-white text-sm whitespace-nowrap cursor-pointer hover:bg-[#2ab3d9d4] duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
    >
      {{ $t('common.pagination.previous') }}
    </button>

    <!-- Páginas Dinámicas -->
    <template v-for="page in visiblePages" :key="page">
      <span
        v-if="page === '...'"
        class="px-2 text-[#7CADB9] cursor-default "
      >{{ page }}</span>
      <button
        v-else
        @click="goToPage(page)"
        class="duration-300"
        :class="[
          ...pageButtonClass(page),
          { 
            'text-sm w-10 ': page.toString().length >= 4,
            'w-10': page.toString().length < 4
          }
        ]"
        :aria-current="page === modelValue ? 'page' : undefined"
      >
        {{ page }}
      </button>
    </template>

    <!-- Botón de siguiente -->
    <button
      @click="goToNextPage"
      :disabled="modelValue === totalPages"
      class="px-4 h-8 flex items-center justify-center rounded-full border border-[#2AB4D9] bg-[#2AB4D9] text-white text-sm whitespace-nowrap cursor-pointer hover:bg-[#2ab3d9d4] disabled:opacity-50 disabled:cursor-not-allowed duration-300"
    >
      {{ $t('common.pagination.next') }}
    </button>
  </nav>
  <span class="hidden md:block" aria-hidden="true"></span>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  totalPages: Number,
  modelValue: Number,
  /**
   * Datos del paginador de la API para el contador de registros:
   * `{ total, from, to }`. Si no se pasa, el contador no se muestra.
   */
  paginationInfo: {
    type: Object,
    default: null
  }
});

const hasPaginationInfo = computed(() => Number.isFinite(props.paginationInfo?.total));

// `from`/`to` pueden venir nulos (p. ej. fuera de rango); se cae al total.
const rangeFrom = computed(() => props.paginationInfo?.from ?? (props.paginationInfo?.total > 0 ? 1 : 0));
const rangeTo = computed(() => props.paginationInfo?.to ?? props.paginationInfo?.total ?? 0);

const emit = defineEmits(['update:modelValue']);

const visiblePages = computed(() => {
  const pages = [];
  const current = props.modelValue;
  const total = props.totalPages;
  const maxAdjacent = 1; // Páginas adyacentes a mostrar

  // Siempre mostrar primera página
  pages.push(1);

  // Rango izquierdo
  if (current - maxAdjacent > 2) {
    pages.push('...');
  }

  // Páginas centrales
  const start = Math.max(2, current - maxAdjacent);
  const end = Math.min(total - 1, current + maxAdjacent);
  
  for (let i = start; i <= end; i++) {
    if (i > 1 && i < total) {
      pages.push(i);
    }
  }

  // Rango derecho
  if (current + maxAdjacent < total - 1) {
    pages.push('...');
  }

  // Siempre mostrar última página (si hay más de 1 página)
  if (total > 1) {
    pages.push(total);
  }

  return pages;
});

const pageButtonClass = (page) => [
  'w-8 h-8 flex items-center justify-center rounded-full border transition-colors',
  page === props.modelValue
    ? 'bg-[#2AB4D9] text-white border-[#2AB4D9] cursor-default'
    : 'bg-white text-[#2AB4D9] border-[#2AB4D9] hover:bg-gray-100'
];

const goToPage = (page) => {
  if (page !== '...') {
    emit('update:modelValue', page);
  }
};

const goToPreviousPage = () => {
  if (props.modelValue > 1) {
    emit('update:modelValue', props.modelValue - 1);
  }
};

const goToNextPage = () => {
  if (props.modelValue < props.totalPages) {
    emit('update:modelValue', props.modelValue + 1);
  }
};
</script>
